import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, Link, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { CompanyService } from '../../../services/CompanyService'
import { loadInviteCsvMaxRows } from '../../../services/OnboardingService'
import WorkspaceInviteLinkCard from '../../CompanyManagement/components/WorkspaceInviteLinkCard'
import { useActorPermissionKeys, useEnsureRbacCompany, useSurface, useSurfaceState } from '../../../hooks/usePermission'
import { useInviteRoles } from '../../../hooks/useInviteRoles'
import {
  assignableRoles,
  effectiveDefaultRoleKey,
  roleKeyOf,
  roleNameOf,
  roleSendValue,
  roleWithoutPicker,
} from '../../CompanyManagement/utils/roleOptions'
import { needPermissionMessage } from '../../../utils/rbacMessages'
import {
  INVITE_CSV_DEFAULT_MAX_ROWS,
  inviteCsvTemplate,
  isValidInviteEmail,
  readInviteCsv,
  type InviteRole,
  type InviteRowError,
  type InviteRowNote,
} from '../inviteCsv'

export type InviteStatus = 'ready' | 'invalid' | 'sending' | 'sent' | 'member' | 'failed'

export interface InviteChip {
  email: string
  firstName: string
  lastName: string
  /** Set by a CSV row that names a role; typed emails take the role picked on the step. */
  role?: InviteRole
  status: InviteStatus
  error?: string
}

/** Invites go out one at a time through the admin dialog's call (D13), a few in flight at once. */
const MAX_IN_FLIGHT = 3

const STATUS_LABEL: Partial<Record<InviteStatus, string>> = {
  invalid: 'not an email',
  sending: 'sending…',
  sent: 'sent',
  member: 'already a member',
  failed: 'failed',
}

const STATUS_COLOR: Record<InviteStatus, 'default' | 'success' | 'error' | 'info'> = {
  ready: 'default',
  invalid: 'error',
  sending: 'default',
  sent: 'success',
  member: 'info',
  failed: 'error',
}

function newChip(email: string, extra: Partial<InviteChip> = {}): InviteChip {
  const clean = email.trim().toLowerCase()
  return {
    email: clean,
    firstName: '',
    lastName: '',
    status: isValidInviteEmail(clean) ? 'ready' : 'invalid',
    ...extra,
  }
}

function resultStatus(result: { success: boolean; error?: string; message?: string }): Pick<InviteChip, 'status' | 'error'> {
  const text = `${result.error || ''} ${result.message || ''}`
  if (/already a member/i.test(text)) return { status: 'member' }
  if (result.success) return { status: 'sent' }
  if (/invalid email/i.test(text)) return { status: 'invalid' }
  return { status: 'failed', error: result.error || 'Could not send.' }
}

function downloadTemplate() {
  const blob = new Blob([inviteCsvTemplate()], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'sevv-invite-template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * "Invite your team" (SC-438): typed emails as chips, a CSV upload, and the workspace link. Each
 * chip shows how its invite went. Inviting is optional: "Skip for now" moves on.
 */
export default function InviteStep({
  companyId,
  busy,
  onDone,
  onBack,
  companyDefaultRole,
}: {
  companyId: string
  busy: boolean
  onDone: () => void
  onBack: () => void
  /**
   * Fallback for the role invites carry when the person can't assign roles and the roles list did
   * not load. Normally the roles API says what the default is.
   */
  companyDefaultRole?: string
}) {
  const [chips, setChips] = useState<InviteChip[]>([])
  const [draft, setDraft] = useState('')
  const [csvErrors, setCsvErrors] = useState<InviteRowError[]>([])
  const [csvNotes, setCsvNotes] = useState<InviteRowNote[]>([])
  const [csvError, setCsvError] = useState('')
  const [maxRows, setMaxRows] = useState(INVITE_CSV_DEFAULT_MAX_ROWS)
  const [sending, setSending] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  // The step can open before the main menu has asked what this person may do (SC-439).
  useEnsureRbacCompany(companyId)
  const invite = useSurfaceState('onboarding.invite-step')
  // Cloud refuses a role other than the company default from someone without `roles.assign`
  // (it stays the backstop); the step doesn't offer it, and CSV rows asking for one are downgraded.
  const mayAssignRoles = useSurface('invite-dialog.role-picker')
  const actorPermissions = useActorPermissionKeys()
  // One roles request per time the step opens, shared with the link card below it: names, the
  // default role and what this person may assign. If it fails, invites carry the default role.
  const rolesState = useInviteRoles(companyId, invite.allowed)
  const rolesReady = rolesState.status === 'ready'
  // The roles list failing doesn't hide the company's own default: `companyDefaultRole` says it.
  const defaultRoleKey = effectiveDefaultRoleKey({
    defaultRoleKey: rolesReady ? rolesState.defaultRoleKey : undefined,
    legacyRole: companyDefaultRole,
  })
  const choices = rolesReady ? assignableRoles(rolesState.roles, actorPermissions, defaultRoleKey) : []
  const canAssignRoles = mayAssignRoles && choices.length > 0
  const [stepRoleKey, setStepRoleKey] = useState<string | null>(null)
  const stepRole = choices.find((r) => r.key === stepRoleKey)?.key ?? defaultRoleKey
  const defaultRoleName = roleNameOf(rolesState.roles, defaultRoleKey)
  // What a typed email (or a CSV row with no role) is invited as.
  const stepSendValue =
    canAssignRoles && stepRole && stepRole !== defaultRoleKey
      ? roleSendValue(stepRole)
      : roleWithoutPicker(companyDefaultRole, defaultRoleKey)

  useEffect(() => {
    void loadInviteCsvMaxRows(INVITE_CSV_DEFAULT_MAX_ROWS).then(setMaxRows)
  }, [])

  const addChips = (incoming: InviteChip[]) =>
    setChips((current) => {
      const known = new Set(current.map((c) => c.email))
      const fresh = incoming.filter((c) => c.email && !known.has(c.email) && known.add(c.email))
      return [...current, ...fresh]
    })

  const commitDraft = (text: string = draft) => {
    const emails = text.split(/[\s,;]+/).filter(Boolean)
    if (emails.length) addChips(emails.map((e) => newChip(e)))
    setDraft('')
  }

  const update = (email: string, patch: Partial<InviteChip>) =>
    setChips((current) => current.map((c) => (c.email === email ? { ...c, ...patch } : c)))

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const { rows, errors, notes, fileError } = readInviteCsv(await file.text(), maxRows, canAssignRoles, {
      assignableKeys: choices.map((r) => r.key),
      defaultRoleKey,
    })
    setCsvError(fileError || '')
    setCsvErrors(errors)
    setCsvNotes(notes)
    addChips(rows.map((r) => newChip(r.email, { firstName: r.firstName, lastName: r.lastName, role: r.role })))
  }

  const toSend = chips.filter((c) => c.status === 'ready' || c.status === 'failed')
  const allDone = chips.length > 0 && toSend.length === 0 && !chips.some((c) => c.status === 'invalid')

  const send = async () => {
    setSending(true)
    const queue = [...toSend]
    const worker = async () => {
      for (let chip = queue.shift(); chip; chip = queue.shift()) {
        update(chip.email, { status: 'sending', error: undefined })
        try {
          const result = await CompanyService.inviteUserToCompanyAdvanced(companyId, {
            email: chip.email,
            role: canAssignRoles && chip.role ? chip.role : stepSendValue,
            firstName: chip.firstName || undefined,
            lastName: chip.lastName || undefined,
            invitationType: 'email',
          })
          update(chip.email, resultStatus(result))
        } catch (err) {
          update(chip.email, { status: 'failed', error: err instanceof Error ? err.message : 'Could not send.' })
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(MAX_IN_FLIGHT, queue.length) }, worker))
    setSending(false)
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" component="h1" fontWeight={800}>
          Bring your team in
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
          An office is only magic with people in it. <b>Everyone’s free during your 30-day trial</b> — add the
          whole company.
        </Typography>
      </Box>

      {invite.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Checking permissions" />
        </Box>
      ) : !invite.allowed ? (
        <>
          <Alert severity="info" data-testid="invite-step-locked">
            {needPermissionMessage('members.invite')}
          </Alert>
          <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
            <Button variant="outlined" onClick={onBack} disabled={busy}>
              Back
            </Button>
            <Button variant="contained" size="large" onClick={onDone} disabled={busy}>
              Continue →
            </Button>
          </Stack>
        </>
      ) : (
        <>
        <Box>
          {canAssignRoles && stepRole ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="invite-step-role-label">Role for the people you invite</InputLabel>
              <Select
                labelId="invite-step-role-label"
                label="Role for the people you invite"
                value={stepRole}
                disabled={sending}
                onChange={(e) => setStepRoleKey(String(e.target.value))}
                data-testid="invite-step-role-picker"
              >
                {choices.map((role) => (
                  <MenuItem key={role.key} value={role.key}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            defaultRoleName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="invite-step-role-readonly">
                Everyone you invite here joins as {defaultRoleName}.
              </Typography>
            )
          )}
          <TextField
            fullWidth
            label="Emails"
            placeholder="name@company.com, another@company.com"
            value={draft}
            disabled={sending}
            onChange={(e) => {
              const value = e.target.value
              if (/[\s,;]$/.test(value)) commitDraft(value)
              else setDraft(value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitDraft()
              }
            }}
            onBlur={() => commitDraft()}
            onPaste={(e) => {
              e.preventDefault()
              commitDraft(`${draft} ${e.clipboardData.getData('text')}`)
            }}
            helperText="Press Enter or comma after each email."
          />
          {chips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }} data-testid="invite-chips">
              {chips.map((chip) => {
                const label = STATUS_LABEL[chip.status]
                // Named only when it differs from what everyone gets by default.
                const chipRoleKey = chip.role ? roleKeyOf(chip.role) : stepRole
                const chipRoleName =
                  canAssignRoles && chipRoleKey && chipRoleKey !== defaultRoleKey
                    ? roleNameOf(rolesState.roles, chipRoleKey)
                    : undefined
                return (
                  <Chip
                    key={chip.email}
                    data-status={chip.status}
                    color={STATUS_COLOR[chip.status]}
                    variant={chip.status === 'ready' ? 'outlined' : 'filled'}
                    title={chip.error}
                    label={`${chip.email}${chipRoleName ? ` · ${chipRoleName}` : ''}${label ? ` · ${label}` : ''}`}
                    onDelete={
                      chip.status === 'sent' || chip.status === 'sending' || sending
                        ? undefined
                        : () => setChips((current) => current.filter((c) => c.email !== chip.email))
                    }
                  />
                )
              })}
            </Box>
          )}
        </Box>

        <Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()} disabled={sending || rolesState.status === 'loading'}>
              Upload CSV
            </Button>
            <Link component="button" type="button" variant="body2" onClick={downloadTemplate}>
              Download template
            </Link>
            <Typography variant="caption" color="text.secondary">
              Up to {maxRows} people per file.
            </Typography>
          </Stack>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} data-testid="invite-csv-input" />
          {csvError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {csvError}
            </Alert>
          )}
          {csvNotes.length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }} data-testid="invite-csv-notes">
              <Typography variant="body2" fontWeight={600}>
                {csvNotes.length === 1
                  ? '1 person will not get the role the file asked for:'
                  : `${csvNotes.length} people will not get the role the file asked for:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {csvNotes.map((n) => (
                  <li key={n.row}>
                    Row {n.row}: {n.note}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          {csvErrors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="invite-csv-errors">
              <Typography variant="body2" fontWeight={600}>
                {csvErrors.length === 1 ? '1 row was skipped:' : `${csvErrors.length} rows were skipped:`}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {csvErrors.map((e) => (
                  <li key={e.row}>
                    Row {e.row}: {e.reason}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </Box>

        <WorkspaceInviteLinkCard companyId={companyId} inviteRoles={rolesState} />

        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center">
          <Button variant="outlined" onClick={onBack} disabled={busy || sending}>
            Back
          </Button>
          <Stack direction="row" spacing={1.5}>
            {!allDone && (
              <Button onClick={onDone} disabled={busy || sending}>
                Skip for now
              </Button>
            )}
            {allDone ? (
              <Button variant="contained" size="large" onClick={onDone} disabled={busy}>
                Continue →
              </Button>
            ) : (
              <Button variant="contained" size="large" onClick={send} disabled={busy || sending || toSend.length === 0 || rolesState.status === 'loading'}>
                {sending ? 'Sending…' : toSend.length === 1 ? 'Send 1 invite' : `Send ${toSend.length} invites`}
              </Button>
            )}
          </Stack>
        </Stack>
        </>
      )}
    </Stack>
  )
}
